'use client'

import { useEffect } from 'react'

import type { AmplitudeConfig } from './schema'

declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void
    }
}

/** Paths excluded from Amplitude autocapture page views (matches ui-react) */
const EXCLUDED_PAGE_VIEW_PATTERNS = ['/depart', '/return', '/checkout']

/**
 * Initializes the Amplitude SDK with autocapture settings matching ui-react.
 *
 * Both nexus and ui-react share the same API key, so the SDK natively shares
 * the device ID cookie — no custom bridging code needed.
 *
 * Flow:
 * 1. Add plugins (SourcePlugin + SnowplowIdsPlugin) so they enrich all events
 * 2. Call amplitude.init() with shared key
 * 3. After init resolves: bridge Amplitude device ID to Snowplow global context
 *
 * Dynamic imports `@amplitude/analytics-browser` in useEffect to keep ~36KB off critical path.
 * Renders nothing (returns null).
 */
export function AmplitudeScript({
    config,
    appVersion,
}: {
    config: AmplitudeConfig
    appVersion: string
}) {
    useEffect(() => {
        async function initAmplitude() {
            const [amplitude, { createSnowplowIdsPlugin }, { createSourcePlugin }] =
                await Promise.all([
                    import('@amplitude/analytics-browser'),
                    import('./snowplow-ids-plugin'),
                    import('./source-plugin'),
                ])

            // Add plugins before init so they enrich all events including the initial page view
            amplitude.add(createSourcePlugin('nexus'))
            amplitude.add(createSnowplowIdsPlugin())

            void amplitude
                .init(config.apiKey, {
                    serverUrl: config.serverUrl,
                    appVersion,
                    autocapture: {
                        attribution: {
                            resetSessionOnNewCampaign: true,
                        },
                        elementInteractions: true,
                        fileDownloads: true,
                        formInteractions: true,
                        pageViews: {
                            trackOn: () => {
                                const path = window.location.pathname
                                return !EXCLUDED_PAGE_VIEW_PATTERNS.some((pattern) =>
                                    path.includes(pattern),
                                )
                            },
                        },
                        sessions: true,
                    },
                })
                .promise.then(() => {
                    // Bridge Amplitude device ID into Snowplow global context
                    const ampDeviceId = amplitude.getDeviceId()
                    if (ampDeviceId && window.snowplow) {
                        window.snowplow('addGlobalContexts', [
                            {
                                schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                                data: { ampClientId: ampDeviceId },
                            },
                        ])
                    }
                })
        }

        void initAmplitude()
    }, [config.apiKey, config.serverUrl, appVersion])

    return null
}
